import React, { useState, useEffect, useCallback } from 'react';
import './GeoTrustDashboard.css';

import { DashboardCountryVaults } from './DashboardCountryVaults';
import { GeoLinkPendingDepositsPanel } from './GeoLinkPendingDepositsPanel';
import { GeoLinkPendingTradesPanel } from './GeoLinkPendingTradesPanel';
import { GeoLinkRulesPanel } from './GeoLinkRulesPanel';
import { ContractExplorer } from './ContractExplorer';
import { RegisteredContractsList } from './RegisteredContractsList';
import { ExecutionRuleManager } from './ExecutionRuleManager';
import { TrustGroupManager } from './TrustGroupManager';

import { getAllDefindexBalances, getKnownDefindexVaults } from '../v2/services/defindexService';
import type { DefindexVaultBalance, KnownVault } from '../v2/services/defindexService';
import { prepareDepositXdr } from '../v2/services/pendingDepositService';
import type { SmartAccountRecord } from '../services/passkeyKitService';
import type { PendingDeposit } from '../v2/types';
import {
  Asset,
  Horizon,
  Networks,
  Operation,
  TransactionBuilder,
  BASE_FEE,
} from '@stellar/stellar-sdk';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { COUNTRY_LIST, countryFlag, PIE_COLORS } from '../v2/countryList';

// ── Prop types ────────────────────────────────────────────────────────────────

interface GeoTrustDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string | null;
  wallet?: {
    signTransaction: (xdr: string, passphrase: string) => Promise<string | { signedTransaction: string }>;
  } | null;
  passkeyKitService?: {
    getAccounts: () => SmartAccountRecord[];
    signUnsignedXdr: (xdr: string, account: SmartAccountRecord, latestLedger?: number) => Promise<string>;
  } | null;
  contractClient?: any | null;
  readOnlyClient?: any | null;
  isAdmin?: boolean;
  mainAdminAddress?: string | null;
  allowedCountries?: Set<number>;
  defaultAllowAll?: boolean;
  totalCountries?: number;
  stellarBalance?: string | null;
  // Map markers (passed through from App.tsx)
  playerLocation?:  [number, number] | null;
  otherUsers?:      Array<{ id: string; location: [number, number]; distance: number }>;
  nearbyNFTs?:      any[];
  nearbyContracts?: any[];
  /** Called when admin changes a country's allowed/denied status — parent updates its allowedCountries state */
  onCountryAllowedChange?: (countryCode: number, allowed: boolean) => void;
  /** Called when admin saves policy/border changes — parent re-fetches allowed countries list */
  onPolicyChanged?: () => void;
  /** Called after a vault deposit — parent should refresh wallet balance */
  onDepositSuccess?: () => void;
  /** Called after wallet→passkey fund transfer — parent should refresh wallet balance */
  onWalletBalanceChange?: () => void;
  /** When provided, jumps straight to this section on open (e.g. 'deposits' from hamburger shortcut) */
  initialSection?: DashSection;
  /** Notifies parent whenever the pending-deposit count changes (for hamburger badge) */
  onPendingCountChange?: (count: number) => void;
  /** Current map style URL — kept in sync with the main map settings panel */
  mapStyle?: string;
  /** Incremented when admin saves border changes; passed to DashboardCountryVaults so its map re-fetches */
  borderRevision?: number;
  /** Called after admin saves border changes — bubbled up from DashboardCountryVaults to App.tsx */
  onBordersChanged?: () => void;
}

// ── Navigation tabs ───────────────────────────────────────────────────────────

type DashSection =
  | 'map'
  | 'defindex'
  | 'deposits'
  | 'contracts'
  | 'trust-groups';

const SECTIONS: { id: DashSection; icon: string; label: string; shortLabel: string; requiresWallet: boolean }[] = [
  { id: 'map',          icon: '🌍', label: 'Map & Vaults',    shortLabel: 'Map',      requiresWallet: false },
  { id: 'defindex',     icon: '📈', label: 'DeFindex',         shortLabel: 'DeFindex', requiresWallet: true  },
  { id: 'deposits',     icon: '💳', label: 'Deposits',         shortLabel: 'Deposits', requiresWallet: true  },
  { id: 'contracts',    icon: '📋', label: 'Smart Contracts',  shortLabel: 'Contracts',requiresWallet: false },
  { id: 'trust-groups', icon: '👥', label: 'Trust Groups',     shortLabel: 'Groups',   requiresWallet: true  },
];

// ── Deposit sub-tabs ──────────────────────────────────────────────────────────

type DepositTab = 'pending' | 'trades' | 'completed' | 'rejected';

// ── Contracts sub-tabs ────────────────────────────────────────────────────────

type ContractsTab = 'explorer' | 'registered' | 'rules';

// ── Main component ────────────────────────────────────────────────────────────

export const GeoTrustDashboard: React.FC<GeoTrustDashboardProps> = ({
  isOpen,
  onClose,
  walletAddress,
  wallet,
  passkeyKitService,
  contractClient,
  readOnlyClient,
  isAdmin = false,
  mainAdminAddress = null,
  allowedCountries = new Set<number>(),
  defaultAllowAll = false,
  totalCountries = 203,
  stellarBalance = null,
  playerLocation,
  otherUsers,
  nearbyNFTs,
  nearbyContracts,
  onCountryAllowedChange,
  onPolicyChanged,
  onDepositSuccess,
  onWalletBalanceChange,
  initialSection,
  onPendingCountChange,
  mapStyle,
  borderRevision,
  onBordersChanged,
}) => {
  const [activeSection, setActiveSection] = useState<DashSection>('map');
  const [depositTab,    setDepositTab]    = useState<DepositTab>('pending');
  const [contractsTab,  setContractsTab]  = useState<ContractsTab>('explorer');

  // Jump to the requested section whenever the dashboard is opened
  useEffect(() => {
    if (isOpen && initialSection) {
      setActiveSection(initialSection);
      if (initialSection === 'deposits') setDepositTab('pending');
    }
  }, [isOpen, initialSection]);

  // DeFindex state
  const [defindexBalances, setDefindexBalances] = useState<DefindexVaultBalance[]>([]);
  const [defindexLoading,  setDefindexLoading]  = useState(false);
  const [defindexVaults,   setDefindexVaults]   = useState<KnownVault[]>([]);

  // Two-step country-vault → DeFindex sourcing wizard
  const [sourcingVault, setSourcingVault] = useState('');
  const [sourcingCountry, setSourcingCountry] = useState('');
  const [sourcingAmount, setSourcingAmount] = useState('');
  const [sourcingStep, setSourcingStep] = useState<'idle' | 'withdraw' | 'deposit' | 'done'>('idle');
  const [sourcingError, setSourcingError] = useState<string | null>(null);
  const [sourcingStatus, setSourcingStatus] = useState<string | null>(null);

  // Direct DeFindex deposit (wallet or passkey)
  const [directVault,  setDirectVault]  = useState('');
  const [directAmount, setDirectAmount] = useState('');
  const [directBusy,   setDirectBusy]   = useState(false);
  const [directError,  setDirectError]  = useState<string | null>(null);
  const [directSuccess,setDirectSuccess]= useState<string | null>(null);
  const [directSigner, setDirectSigner] = useState<'wallet' | 'passkey'>('wallet');
  const [directSignerBalance, setDirectSignerBalance] = useState<number | null>(null);

  // Deposit overlay
  const [showDepositOverlay,  setShowDepositOverlay]  = useState(false);
  const [depositOverlayTab,   setDepositOverlayTab]   = useState<'direct' | 'from-vault'>('direct');

  // Country picker (sourcing wizard)
  const [countrySearch,       setCountrySearch]       = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Fetch available balance for the active depositor whenever the overlay is open or signer changes
  useEffect(() => {
    if (!showDepositOverlay || depositOverlayTab !== 'direct' || !walletAddress) {
      setDirectSignerBalance(null);
      return;
    }
    const isWalletPasskey = walletAddress.startsWith('C');
    const linkedPK = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;
    const usePasskey = isWalletPasskey || (directSigner === 'passkey' && !!linkedPK);
    const addr = usePasskey
      ? (isWalletPasskey ? walletAddress : linkedPK?.accountAddress)
      : walletAddress;
    if (!addr) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/passkey-kit/account-balance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountAddress: addr }),
    })
      .then(r => r.json())
      .then(d => { if (d.success) setDirectSignerBalance(d.balance / 10_000_000); })
      .catch(() => {});
  }, [showDepositOverlay, depositOverlayTab, walletAddress, directSigner, passkeyKitService]);

  // ── Load DeFindex balances ─────────────────────────────────────────────────

  const loadDefindex = useCallback(async () => {
    if (!walletAddress) return;
    setDefindexLoading(true);
    try {
      const vaults = await getKnownDefindexVaults();
      setDefindexVaults(vaults);

      // Fetch wallet (G-address) balances
      const walletResults = await getAllDefindexBalances(walletAddress);

      // Also fetch for the linked passkey smart account, if any
      const linkedAccount = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;
      if (!linkedAccount) {
        setDefindexBalances(walletResults);
        return;
      }

      const passkeyResults = await getAllDefindexBalances(linkedAccount.accountAddress);

      // Merge: combine wallet + passkey underlying XLM per vault
      const merged = walletResults.map((wb, i) => {
        const pb = passkeyResults[i];
        const walletXlm   = parseFloat(wb.underlyingXlm) || 0;
        const passkeyXlm  = pb && !pb.error ? (parseFloat(pb.underlyingXlm) || 0) : 0;
        const combinedXlm = walletXlm + passkeyXlm;
        const combinedStroops = BigInt(wb.underlyingStroops || '0') +
                                BigInt((pb && !pb.error && pb.underlyingStroops) ? pb.underlyingStroops : '0');
        // Combined share balance (for hasPosition check)
        const combinedShares = BigInt(wb.shareBalance || '0') +
                               BigInt((pb && !pb.error && pb.shareBalance) ? pb.shareBalance : '0');
        return {
          ...wb,
          underlyingXlm:        combinedXlm.toFixed(7),
          underlyingStroops:    String(combinedStroops),
          shareBalance:         String(combinedShares),
          shareBalanceDisplay:  (Number(combinedShares) / 10_000_000).toFixed(7),
          walletUnderlyingXlm:  wb.underlyingXlm,
          passkeyUnderlyingXlm: pb && !pb.error ? pb.underlyingXlm : '0.0000000',
          walletShareBalance:   wb.shareBalance,
          passkeyShareBalance:  pb && !pb.error ? pb.shareBalance : '0',
        } as DefindexVaultBalance;
      });

      setDefindexBalances(merged);
    } catch (e) {
      // non-fatal — leave stale data
    } finally {
      setDefindexLoading(false);
    }
  }, [walletAddress, passkeyKitService]);

  useEffect(() => {
    if (isOpen && walletAddress && activeSection === 'defindex') {
      loadDefindex();
    }
  }, [isOpen, walletAddress, activeSection, loadDefindex]);

  // Also load on map section
  useEffect(() => {
    if (isOpen && walletAddress && activeSection === 'map') {
      loadDefindex();
    }
  }, [isOpen, walletAddress, activeSection, loadDefindex]);

  // ── Passkey signer ────────────────────────────────────────────────────────
  const handleSignXdrPasskey = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!passkeyKitService) throw new Error('Passkey service unavailable. Reload and try again.');
    const accounts = passkeyKitService.getAccounts();
    if (!accounts.length) throw new Error('No passkey account found. Register a passkey first.');
    // accountAddress is the smart-contract C-address; walletAddress is the linked Freighter G-address
    const passKeyAddr = walletAddress?.startsWith('C') ? walletAddress : accounts[0].accountAddress;
    const prep = await prepareDepositXdr(deposit.pendingDepositId, amountStroops, passKeyAddr, { useServiceAccount: true });
    if (!prep.success || !prep.unsignedXdr) throw new Error(prep.error || 'Failed to prepare deposit transaction');
    // Find the passkey account record by its contract address (accountAddress), not walletAddress
    const account = accounts.find(a => a.accountAddress === passKeyAddr) ?? accounts[0];
    const signedXdr = await passkeyKitService.signUnsignedXdr(prep.unsignedXdr, account, prep.latestLedger);
    return { signedXdr, useServiceAccount: true };
  }, [walletAddress, passkeyKitService]);

  // ── Wallet signer ─────────────────────────────────────────────────────────
  const handleSignXdrWallet = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!wallet || !walletAddress) throw new Error('No wallet connected');
    const prep = await prepareDepositXdr(deposit.pendingDepositId, amountStroops, walletAddress, { useServiceAccount: false });
    if (!prep.success || !prep.unsignedXdr) throw new Error(prep.error || 'Failed to prepare deposit transaction');
    const passphrase = deposit.network === 'mainnet'
      ? 'Public Global Stellar Network ; September 2015'
      : 'Test SDF Network ; September 2015';
    const result = await wallet.signTransaction(prep.unsignedXdr, passphrase);
    return { signedXdr: typeof result === 'string' ? result : result.signedTransaction, useServiceAccount: false };
  }, [walletAddress, wallet]);

  // ── Auto signer (single method connected) ────────────────────────────────
  const handleSignXdr = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!walletAddress) throw new Error('No wallet connected');
    if (walletAddress.startsWith('C')) return handleSignXdrPasskey(deposit, amountStroops);
    return handleSignXdrWallet(deposit, amountStroops);
  }, [walletAddress, handleSignXdrPasskey, handleSignXdrWallet]);

  const hasBothSigners = !!(wallet && passkeyKitService && passkeyKitService.getAccounts().length > 0);

  // ── Two-step country-vault sourcing ───────────────────────────────────────

  const handleStartSourcing = async () => {
    if (!sourcingCountry || !sourcingAmount || !sourcingVault || !walletAddress) {
      setSourcingError('Fill in country, DeFindex vault, and amount.');
      return;
    }
    const amountNum = parseFloat(sourcingAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setSourcingError('Enter a valid positive amount.');
      return;
    }

    setSourcingError(null);
    setSourcingStep('withdraw');
    setSourcingStatus('Step 1 of 2: Withdrawing from country vault…');

    try {
      const amountStroops = Math.round(amountNum * 10_000_000);

      // Step 1: Withdraw from GeoTrust country vault
      const withdrawResp = await fetch('/api/v2/vault/withdraw', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userAddress:  walletAddress,
          country:      sourcingCountry,
          assetAddress: process.env.REACT_APP_STELLAR_ASSET_CONTRACT || 'native',
          amount:       amountStroops,
        }),
      });
      const withdrawData = await withdrawResp.json();
      if (!withdrawData.success) {
        throw new Error(withdrawData.error || 'Withdrawal failed');
      }
      setSourcingStatus(`Step 1 complete ✓  Tx: ${(withdrawData.txHash || '').slice(0, 8)}…  Step 2: Finding DeFindex deposit…`);
      setSourcingStep('deposit');

      // Step 2: The withdrawn XLM is now in the user's wallet.
      // Auto-navigate to Deposits tab so user can execute the DeFindex deposit.
      setTimeout(() => {
        setSourcingStep('done');
        setSourcingStatus(null);
        setActiveSection('deposits');
        setDepositTab('pending');
      }, 1500);
    } catch (err: any) {
      setSourcingError(err.message || 'Sourcing failed');
      setSourcingStep('idle');
      setSourcingStatus(null);
    }
  };

  const resetSourcing = () => {
    setSourcingStep('idle');
    setSourcingError(null);
    setSourcingStatus(null);
    setSourcingAmount('');
    setSourcingCountry('');
  };

  // ── Direct DeFindex Deposit (wallet or passkey) ──────────────────────────

  const handleDirectDeposit = async () => {
    if (!walletAddress || !directVault || !directAmount) {
      setDirectError('Select a vault and enter an amount.');
      return;
    }
    const amountNum = parseFloat(directAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setDirectError('Enter a valid positive amount.');
      return;
    }
    if (directSignerBalance !== null && amountNum > directSignerBalance) {
      setDirectError(
        `Insufficient balance: your account holds ${directSignerBalance.toFixed(4)} XLM but you tried to deposit ${amountNum.toFixed(4)} XLM.`
      );
      return;
    }
    setDirectError(null);
    setDirectSuccess(null);
    setDirectBusy(true);
    try {
      const amountStroops = Math.round(amountNum * 10_000_000);

      // Resolve the depositor address and signing method
      const isWalletPasskey = walletAddress.startsWith('C');
      const linkedAccount   = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;
      const usePasskey      = isWalletPasskey || (directSigner === 'passkey' && !!linkedAccount);
      const depositorAddress = usePasskey
        ? (isWalletPasskey ? walletAddress : linkedAccount!.accountAddress)
        : walletAddress;

      // Build unsigned XDR via backend
      const prepResp = await fetch('/api/v2/defindex/deposit-xdr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vaultAddress:      directVault,
          amount:            amountStroops,
          userPublicKey:     depositorAddress,
          useServiceAccount: usePasskey,
        }),
      });
      const prep = await prepResp.json();
      if (!prep.success || !prep.unsignedXdr) throw new Error(prep.error || 'Failed to build transaction');

      let signedXdr: string;
      let useServiceAccount = false;

      if (usePasskey) {
        if (!passkeyKitService) throw new Error('Passkey service unavailable.');
        const accounts = passkeyKitService.getAccounts();
        const account  = accounts.find((a: any) => a.accountAddress === depositorAddress) ?? accounts[0];
        if (!account) throw new Error('No passkey account found. Register a passkey first.');
        signedXdr = await passkeyKitService.signUnsignedXdr(prep.unsignedXdr, account, prep.latestLedger);
        useServiceAccount = true;
      } else if (wallet) {
        const result = await wallet.signTransaction(
          prep.unsignedXdr,
          'Test SDF Network ; September 2015',
        );
        signedXdr = typeof result === 'string' ? result : result.signedTransaction;
      } else {
        throw new Error('No wallet connected. Connect a wallet or set up passkeys.');
      }

      // Submit
      const submitResp = await fetch('/api/v2/defindex/deposit-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signedXdr, useServiceAccount }),
      });
      const submitData = await submitResp.json();
      if (!submitData.success) throw new Error(submitData.error || 'Submission failed');

      setDirectSuccess(`Deposit successful! Tx: ${(submitData.txHash || '').slice(0, 12)}…`);
      setDirectAmount('');
      loadDefindex(); // refresh balances
    } catch (err: any) {
      const raw: string = err.message || 'Deposit failed';
      // Parse simulation errors into friendly messages
      const insufficientMatch = raw.match(/balance is not sufficient to spend.*?amount:\s*(\d+)/s);
      if (insufficientMatch) {
        const availXlm = (parseInt(insufficientMatch[1], 10) / 10_000_000).toFixed(4);
        setDirectError(`Insufficient balance: your account holds ${availXlm} XLM but you tried to deposit ${amountNum.toFixed(4)} XLM.`);
      } else if (raw.includes('Simulation failed')) {
        // Strip the verbose diagnostic log — keep just the first meaningful line
        const firstLine = raw.split('\n')[0].replace('Simulation failed: ', '');
        setDirectError(`Simulation failed: ${firstLine}`);
      } else {
        setDirectError(raw);
      }
    } finally {
      setDirectBusy(false);
    }
  };

  // ── Total DeFindex value (shown in section header) ────────────────────────

  const totalDefindexXlm = defindexBalances.reduce(
    (sum, b) => sum + (parseFloat(b.underlyingXlm) || 0),
    0,
  );

  // ── Chart data ─────────────────────────────────────────────────────────────

  const pieData = defindexBalances
    .filter(b => parseFloat(b.underlyingXlm || '0') > 0)
    .map(b => ({ name: b.vaultName || 'Vault', value: parseFloat(b.underlyingXlm) }));

  const barData = defindexBalances.map(b => ({
    name: (b.vaultName || 'Vault').split(' ')[0],
    'Wallet':  parseFloat(b.walletUnderlyingXlm  !== undefined ? b.walletUnderlyingXlm  : b.underlyingXlm  || '0'),
    'Passkey': parseFloat(b.passkeyUnderlyingXlm !== undefined ? b.passkeyUnderlyingXlm : '0'),
  }));

  // ── Visible sections (filter wallet-required ones) ─────────────────────────

  const visibleSections = SECTIONS.filter(s => !s.requiresWallet || !!walletAddress);

  if (!isOpen) return null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="gtd-shell">
      {/* ── Top bar ────────────────────────────────────────────────────── */}
      <div className="gtd-topbar">
        <button className="gtd-back-btn" onClick={onClose}>
          ← Back to Map
        </button>
        <div className="gtd-title">
          GeoTrust <span>Dashboard</span>
        </div>
        {walletAddress && (
          <div className="gtd-wallet-chip">
            <span>🔑</span>
            <span className="addr">{walletAddress}</span>
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="gtd-body">

        {/* ── Sidebar (desktop) ────────────────────────────────────────── */}
        <nav className="gtd-sidebar">
          <div className="gtd-sidebar-section">
            <div className="gtd-sidebar-label">Navigation</div>
            {visibleSections.map(s => (
              <button
                key={s.id}
                className={`gtd-nav-item${activeSection === s.id ? ' active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                <span className="gtd-nav-icon">{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* ── Content ──────────────────────────────────────────────────── */}
        <main className={`gtd-content${activeSection === 'map' ? ' gtd-map-mode' : ''}`}>

          {/* ── MAP & VAULTS ────────────────────────────────────────────── */}
          {activeSection === 'map' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <DashboardCountryVaults
                walletAddress={walletAddress}
                wallet={wallet as any}
                passkeyKitService={passkeyKitService as any}
                contractClient={contractClient}
                readOnlyClient={readOnlyClient}
                mainAdminAddress={mainAdminAddress}
                isAdmin={isAdmin}
                allowedCountries={allowedCountries}
                defaultAllowAll={defaultAllowAll}
                totalCountries={totalCountries}
                stellarBalance={stellarBalance}
                allowedCount={defaultAllowAll ? (totalCountries - allowedCountries.size) : allowedCountries.size}
                onDepositSuccess={onDepositSuccess}
                onWalletBalanceChange={onWalletBalanceChange}
                onNavigate={(id) => setActiveSection(id as DashSection)}
                onPolicyChanged={onPolicyChanged}
                onCountryAllowedChange={onCountryAllowedChange}
                playerLocation={playerLocation}
                otherUsers={otherUsers}
                nearbyNFTs={nearbyNFTs}
                nearbyContracts={nearbyContracts}
                mapStyle={mapStyle}
                borderRevision={borderRevision}
                onBordersChanged={onBordersChanged}
              />
            </div>
          )}

          {/* ── DEFINDEX VAULTS ────────────────────────────────────────── */}
          {activeSection === 'defindex' && (
            <div className="gtd-defindex-page">

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
                <div>
                  <h2 className="gtd-section-title">DeFindex Vaults</h2>
                  <p className="gtd-section-subtitle">
                    Your yield-bearing vault positions
                    {totalDefindexXlm > 0 && (
                      <strong style={{ marginLeft: 8, color: 'var(--gt-gold)' }}>
                        — {totalDefindexXlm.toFixed(4)} XLM total
                      </strong>
                    )}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="gtd-btn gtd-btn-outline gtd-btn-sm" onClick={loadDefindex} disabled={defindexLoading}>
                    {defindexLoading ? <span className="gtd-spin" /> : '↻'} Refresh
                  </button>
                  {walletAddress && (
                    <button className="gtd-btn gtd-btn-primary gtd-btn-sm"
                      onClick={() => { setShowDepositOverlay(true); setDepositOverlayTab('direct'); }}>
                      ⬆ Deposit
                    </button>
                  )}
                </div>
              </div>

              {/* Body */}
              {defindexLoading ? (
                <div className="gtd-loading"><span className="gtd-spin" /> Loading vault balances…</div>
              ) : !walletAddress ? (
                <div className="gtd-empty"><span className="icon">📈</span>Connect a wallet to view your DeFindex positions.</div>
              ) : defindexBalances.length === 0 ? (
                <div className="gtd-empty">
                  <span className="icon">📈</span>
                  No vault positions found. Make your first deposit!
                  <button className="gtd-btn gtd-btn-primary" style={{ marginTop: 16 }}
                    onClick={() => { setShowDepositOverlay(true); setDepositOverlayTab('direct'); }}>
                    ⬆ Deposit Now
                  </button>
                </div>
              ) : (
                <div className="gtd-defindex-split">

                  {/* ── Left: My Positions ───────────────────────────── */}
                  <div className="gtd-defindex-positions">
                    <div className="gtd-panel-label">My Positions</div>
                    {defindexBalances.map(b => (
                      <DefindexVaultCard key={b.vaultAddress} balance={b}
                        onDeposit={() => { setShowDepositOverlay(true); setDepositOverlayTab('direct'); }} />
                    ))}
                  </div>

                  {/* ── Right: Charts ────────────────────────────────── */}
                  <div className="gtd-defindex-charts">
                    <div className="gtd-panel-label">Portfolio Overview</div>

                    {/* Mini stat strip */}
                    <div className="gtd-stats-mini">
                      <div className="gtd-stat-mini">
                        <div className="sm-label">Total Invested</div>
                        <div className="sm-value">{totalDefindexXlm.toFixed(4)} XLM</div>
                      </div>
                      <div className="gtd-stat-mini">
                        <div className="sm-label">Active Vaults</div>
                        <div className="sm-value">
                          {defindexBalances.filter(b => parseFloat(b.underlyingXlm) > 0).length}
                        </div>
                      </div>
                      <div className="gtd-stat-mini">
                        <div className="sm-label">💳 Wallet</div>
                        <div className="sm-value">
                          {defindexBalances.reduce((s, b) => s + parseFloat(b.walletUnderlyingXlm !== undefined ? b.walletUnderlyingXlm : b.underlyingXlm || '0'), 0).toFixed(4)}
                        </div>
                      </div>
                      <div className="gtd-stat-mini">
                        <div className="sm-label">🔑 Passkey</div>
                        <div className="sm-value">
                          {defindexBalances.reduce((s, b) => s + parseFloat(b.passkeyUnderlyingXlm || '0'), 0).toFixed(4)}
                        </div>
                      </div>
                    </div>

                    {/* Vault allocation pie chart */}
                    {pieData.length > 0 && (
                      <div className="gtd-chart-card">
                        <div className="gtd-chart-title">Vault Allocation</div>
                        <ResponsiveContainer width="100%" height={210}>
                          <PieChart>
                            <Pie data={pieData} cx="50%" cy="50%" outerRadius={78}
                              dataKey="value" labelLine={false}
                              label={({ name, percent }: any) =>
                                `${(name as string).split(' ')[0]} ${((percent as number) * 100).toFixed(0)}%`}
                            >
                              {pieData.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any) => [`${Number(v).toFixed(4)} XLM`, 'Value']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Funding source bar chart */}
                    {barData.length > 0 && (
                      <div className="gtd-chart-card">
                        <div className="gtd-chart-title">Funding Source by Vault</div>
                        <ResponsiveContainer width="100%" height={170}>
                          <BarChart data={barData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: any) => `${v}`} />
                            <Tooltip formatter={(v: any) => [`${Number(v).toFixed(4)} XLM`]} />
                            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Wallet"  fill="#0ea5e9" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="Passkey" fill="#10b981" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Deposit overlay ──────────────────────────────────── */}
              {showDepositOverlay && (
                <div className="gtd-overlay-backdrop" onClick={() => setShowDepositOverlay(false)}>
                  <div className="gtd-overlay-panel" onClick={e => e.stopPropagation()}>

                    <div className="gtd-overlay-header">
                      <span className="gtd-overlay-title">⬆ DeFindex Deposit</span>
                      <button className="gtd-overlay-close" onClick={() => setShowDepositOverlay(false)}>✕</button>
                    </div>

                    <div className="gtd-subtabs" style={{ padding: '0 20px', borderBottom: '2px solid var(--gt-border)', marginBottom: 0 }}>
                      {(['direct', 'from-vault'] as const).map(t => (
                        <button key={t}
                          className={`gtd-subtab${depositOverlayTab === t ? ' active' : ''}`}
                          onClick={() => setDepositOverlayTab(t)}
                        >
                          {t === 'direct' ? '⬆ Direct Deposit' : '🔄 From Country Vault'}
                        </button>
                      ))}
                    </div>

                    <div className="gtd-overlay-body">

                      {/* Direct deposit tab */}
                      {depositOverlayTab === 'direct' && (() => {
                        const isWalletPasskey = walletAddress?.startsWith('C');
                        const linkedPK = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;
                        const hasBothDirect = !isWalletPasskey && !!linkedPK;
                        const effectiveSigner = isWalletPasskey ? 'passkey' : directSigner;
                        return (
                          <div>
                            <p style={{ fontSize: 13, color: 'var(--gt-muted)', marginBottom: 16 }}>
                              Deposit XLM directly into a DeFindex vault from your{' '}
                              {effectiveSigner === 'passkey' ? 'passkey smart account' : 'connected wallet'}.
                            </p>

                            {hasBothDirect && (
                              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                                {(['wallet', 'passkey'] as const).map(s => (
                                  <button key={s} onClick={() => setDirectSigner(s)} style={{
                                    flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13,
                                    fontWeight: 600, border: '1.5px solid', cursor: 'pointer', transition: 'all .15s',
                                    borderColor: effectiveSigner === s ? 'var(--gt-gold)' : 'var(--gt-border)',
                                    background:  effectiveSigner === s ? 'var(--gt-gold-bg)' : '#fff',
                                    color:       effectiveSigner === s ? 'var(--gt-gold)' : 'var(--gt-muted)',
                                  }}>
                                    {s === 'wallet' ? '💳 Wallet' : '🔑 Passkey'}
                                  </button>
                                ))}
                              </div>
                            )}
                            {hasBothDirect && effectiveSigner === 'passkey' && (
                              <div style={{ fontSize: 11, color: 'var(--gt-muted)', marginBottom: 12,
                                background: 'var(--gt-surface)', padding: '6px 10px', borderRadius: 6,
                                border: '1px solid var(--gt-border)' }}>
                                🔑 Signs with {linkedPK!.accountAddress.slice(0, 8)}…{linkedPK!.accountAddress.slice(-6)}
                              </div>
                            )}

                            <div className="gtd-field">
                              <label>DeFindex Vault</label>
                              <select value={directVault} onChange={e => setDirectVault(e.target.value)}>
                                <option value="">Select vault…</option>
                                {defindexVaults.map(v => (
                                  <option key={v.address} value={v.address}>{v.name} — {v.address.slice(0, 14)}…</option>
                                ))}
                              </select>
                            </div>
                            <div className="gtd-field">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                                <label style={{ margin: 0 }}>Amount (XLM)</label>
                                {directSignerBalance !== null && (
                                  <span style={{ fontSize: 11, color: 'var(--gt-muted)' }}>
                                    Available: <strong style={{ color: 'var(--gt-text)' }}>{directSignerBalance.toFixed(4)} XLM</strong>
                                    <button
                                      onClick={() => setDirectAmount(directSignerBalance.toFixed(7))}
                                      style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                        border: '1px solid var(--gt-border)', background: 'var(--gt-surface)',
                                        cursor: 'pointer', color: 'var(--gt-gold)', fontWeight: 700 }}>
                                      MAX
                                    </button>
                                  </span>
                                )}
                              </div>
                              <input type="number" min="0" step="0.1" placeholder="e.g. 10"
                                value={directAmount} onChange={e => setDirectAmount(e.target.value)}
                                style={directSignerBalance !== null && parseFloat(directAmount) > directSignerBalance
                                  ? { borderColor: '#e53e3e' } : {}} />
                            </div>
                            {directError   && <div className="gtd-notice error">⚠ {directError}</div>}
                            {directSuccess && <div className="gtd-notice success">✓ {directSuccess}</div>}
                            <button className="gtd-btn gtd-btn-primary" style={{ width: '100%', marginTop: 8 }}
                              onClick={handleDirectDeposit} disabled={directBusy || !directVault || !directAmount}>
                              {directBusy ? <><span className="gtd-spin" /> Depositing…</> : '⬆ Deposit Now'}
                            </button>
                          </div>
                        );
                      })()}

                      {/* From country vault tab */}
                      {depositOverlayTab === 'from-vault' && (
                        <div>
                          <p style={{ fontSize: 13, color: 'var(--gt-muted)', marginBottom: 16 }}>
                            Withdraw XLM from a GeoTrust country vault and deposit it into DeFindex.
                          </p>

                          <div className="gtd-steps" style={{ marginBottom: 20 }}>
                            <div className={`gtd-step${sourcingStep !== 'idle' ? ' done' : ''}`}>
                              <div className="gtd-step-num">1</div>
                              <div className="gtd-step-label">Withdraw from country vault</div>
                            </div>
                            <div className={`gtd-step${sourcingStep === 'deposit' || sourcingStep === 'done' ? ' done' : ''}`}>
                              <div className="gtd-step-num">2</div>
                              <div className="gtd-step-label">Deposit into DeFindex vault</div>
                            </div>
                          </div>

                          {sourcingStep === 'idle' && walletAddress && (
                            <div className="gtd-sourcing-form" style={{ marginTop: 0 }}>

                              {/* Country picker */}
                              <div className="gtd-field">
                                <label>Country</label>
                                <div className="gtd-country-picker">
                                  <input
                                    type="text"
                                    placeholder="🔍 Search country…"
                                    value={
                                      sourcingCountry && !showCountryDropdown
                                        ? `${countryFlag(sourcingCountry)} ${COUNTRY_LIST.find(c => c.iso2 === sourcingCountry)?.name ?? sourcingCountry} (${sourcingCountry})`
                                        : countrySearch
                                    }
                                    onChange={e => {
                                      setCountrySearch(e.target.value);
                                      setSourcingCountry('');
                                      setShowCountryDropdown(true);
                                    }}
                                    onFocus={() => {
                                      if (sourcingCountry) setCountrySearch('');
                                      setShowCountryDropdown(true);
                                    }}
                                    onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                                    autoComplete="off"
                                    style={{ width: '100%' }}
                                  />
                                  {showCountryDropdown && (
                                    <div className="gtd-country-dropdown">
                                      {COUNTRY_LIST
                                        .filter(c =>
                                          !countrySearch ||
                                          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                          c.iso2.toLowerCase().startsWith(countrySearch.toLowerCase())
                                        )
                                        .slice(0, 30)
                                        .map(c => (
                                          <div key={c.iso2} className="gtd-country-option"
                                            onMouseDown={() => {
                                              setSourcingCountry(c.iso2);
                                              setCountrySearch('');
                                              setShowCountryDropdown(false);
                                            }}>
                                            <span>{countryFlag(c.iso2)}</span>
                                            <span>{c.name}</span>
                                            <span className="gtd-country-code">{c.iso2}</span>
                                          </div>
                                        ))
                                      }
                                      {COUNTRY_LIST.filter(c =>
                                        !countrySearch ||
                                        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                        c.iso2.toLowerCase().startsWith(countrySearch.toLowerCase())
                                      ).length === 0 && (
                                        <div style={{ padding: '12px 16px', color: 'var(--gt-muted)', fontSize: 13 }}>
                                          No countries found
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="gtd-field">
                                <label>DeFindex Vault</label>
                                <select value={sourcingVault} onChange={e => setSourcingVault(e.target.value)}>
                                  <option value="">Select vault…</option>
                                  {defindexVaults.map(v => (
                                    <option key={v.address} value={v.address}>{v.name} — {v.address.slice(0, 12)}…</option>
                                  ))}
                                </select>
                              </div>
                              <div className="gtd-field">
                                <label>Amount (XLM)</label>
                                <input type="number" min="0" step="0.1" placeholder="e.g. 10"
                                  value={sourcingAmount} onChange={e => setSourcingAmount(e.target.value)} />
                              </div>
                              {sourcingError && <div className="gtd-notice error">⚠ {sourcingError}</div>}
                              <button className="gtd-btn gtd-btn-primary" style={{ width: '100%' }}
                                onClick={handleStartSourcing}>
                                Start: Withdraw &amp; Deposit
                              </button>
                              <div className="gtd-notice info" style={{ marginTop: 4 }}>
                                ℹ Two separate transactions — withdraw then deposit. You'll sign each one.
                              </div>
                            </div>
                          )}

                          {(sourcingStep === 'withdraw' || sourcingStep === 'deposit') && (
                            <div className="gtd-notice warning"><span className="gtd-spin" /> {sourcingStatus}</div>
                          )}

                          {sourcingStep === 'done' && (
                            <div>
                              <div className="gtd-notice success">
                                ✓ Withdrawal complete! Head to the <strong>Deposits</strong> tab to execute the DeFindex deposit.
                              </div>
                              <button className="gtd-btn gtd-btn-ghost gtd-btn-sm" onClick={resetSourcing}>Reset</button>
                            </div>
                          )}

                          {!walletAddress && (
                            <div className="gtd-notice info">ℹ Connect a wallet to use this feature.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── DEPOSITS ───────────────────────────────────────────────── */}
          {activeSection === 'deposits' && (
            <div>
              <div className="gtd-section-header">
                <h2 className="gtd-section-title">Deposits</h2>
                <p className="gtd-section-subtitle">
                  GeoLink-qualified deposit executions — pending, completed, and rejected
                </p>
              </div>

              <div className="gtd-subtabs">
                {(['pending', 'trades', 'completed', 'rejected'] as DepositTab[]).map(t => (
                  <button
                    key={t}
                    className={`gtd-subtab${depositTab === t ? ' active' : ''}`}
                    onClick={() => setDepositTab(t)}
                  >
                    {t === 'trades'
                      ? 'Zenex Trades'
                      : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {depositTab === 'pending' && walletAddress && (
                <GeoLinkPendingDepositsPanel
                  walletPublicKey={walletAddress}
                  passkeyAccountAddress={
                    walletAddress?.startsWith('C')
                      ? walletAddress
                      : ((passkeyKitService as any)?.getAccounts?.()[0]?.accountAddress ?? null)
                  }
                  onSignXdr={hasBothSigners ? undefined : handleSignXdr}
                  onSignXdrWallet={hasBothSigners ? handleSignXdrWallet : undefined}
                  onSignXdrPasskey={hasBothSigners ? handleSignXdrPasskey : undefined}
                  hasBothSigners={hasBothSigners}
                  onPendingCountChange={onPendingCountChange}
                  allowedCountries={allowedCountries}
                />
              )}

              {depositTab === 'trades' && walletAddress && (
                <GeoLinkPendingTradesPanel
                  walletPublicKey={walletAddress}
                  pollIntervalMs={15_000}
                  signUnsignedXdr={
                    passkeyKitService
                      ? async (xdr, latestLedger) => {
                          const accounts = passkeyKitService.getAccounts();
                          if (!accounts.length) throw new Error('No passkey account registered.');
                          const account = walletAddress.startsWith('C')
                            ? accounts.find(a => a.accountAddress === walletAddress) ?? accounts[0]
                            : accounts[0];
                          return passkeyKitService.signUnsignedXdr(xdr, account, latestLedger);
                        }
                      : undefined
                  }
                  signClassicXdr={
                    wallet
                      ? async (xdr, networkPassphrase) => {
                          const res = await wallet.signTransaction(xdr, networkPassphrase);
                          return typeof res === 'string' ? res : res.signedTransaction;
                        }
                      : undefined
                  }
                  activePasskey={
                    passkeyKitService
                      ? (() => {
                          const accounts = passkeyKitService.getAccounts();
                          if (!accounts.length) return null;
                          return walletAddress.startsWith('C')
                            ? accounts.find(a => a.accountAddress === walletAddress) ?? accounts[0]
                            : accounts[0];
                        })()
                      : null
                  }
                  /*
                   * One-click trustline establishment for the
                   * `missing_trustline` error path. Wired only for
                   * G-address wallets — smart accounts (C-addresses)
                   * don't hold classic Stellar trustlines, so the
                   * button stays hidden for them (the panel guards on
                   * `onAddTrustline` being defined).
                   *
                   * Mechanics: load the user's account from Horizon
                   * for a fresh sequence number, build a single
                   * `changeTrust` op (no explicit limit = max), sign
                   * with the connected classic wallet, submit through
                   * Horizon. We throw on submission failure so the
                   * panel's `captureError` surfaces the precise reason
                   * (insufficient base reserve, user rejected, etc.)
                   * in the same UI it shows for trade errors.
                   */
                  onAddTrustline={
                    wallet && walletAddress && !walletAddress.startsWith('C')
                      ? async (asset: { code: string; issuer: string }) => {
                          const horizonUrl =
                            process.env.REACT_APP_HORIZON_URL ||
                            process.env.REACT_APP_STELLAR_HORIZON_URL ||
                            'https://horizon-testnet.stellar.org';
                          const networkPassphrase =
                            process.env.REACT_APP_STELLAR_NETWORK_PASSPHRASE ||
                            Networks.TESTNET;
                          const server = new Horizon.Server(horizonUrl);
                          const source = await server.loadAccount(walletAddress);
                          const tx = new TransactionBuilder(source, {
                            fee: BASE_FEE,
                            networkPassphrase,
                          })
                            .addOperation(Operation.changeTrust({
                              asset: new Asset(asset.code, asset.issuer),
                            }))
                            .setTimeout(60)
                            .build();
                          const signRes = await wallet.signTransaction(
                            tx.toXDR(),
                            networkPassphrase,
                          );
                          const signedXdr =
                            typeof signRes === 'string'
                              ? signRes
                              : signRes.signedTransaction;
                          const signed = TransactionBuilder.fromXDR(
                            signedXdr,
                            networkPassphrase,
                          );
                          await server.submitTransaction(signed as any);
                        }
                      : undefined
                  }
                />
              )}

              {depositTab === 'completed' && (
                <GeoLinkRulesPanel mode="completed" walletPublicKey={walletAddress} allowedCountries={allowedCountries} />
              )}

              {depositTab === 'rejected' && (
                <GeoLinkRulesPanel mode="rejected" walletPublicKey={walletAddress} allowedCountries={allowedCountries} />
              )}

              {(depositTab === 'pending' || depositTab === 'trades') && !walletAddress && (
                <div className="gtd-notice info">ℹ Connect a wallet to see pending actions.</div>
              )}
            </div>
          )}

          {/* ── SMART CONTRACTS ────────────────────────────────────────── */}
          {activeSection === 'contracts' && (
            <div className="gtd-section-contracts">
              <div className="gtd-section-header">
                <h2 className="gtd-section-title">Smart Contracts</h2>
                <p className="gtd-section-subtitle">
                  Browse contracts and manage GeoLink execution rules
                </p>
              </div>

              <div className="gtd-subtabs">
                <button
                  className={`gtd-subtab${contractsTab === 'explorer' ? ' active' : ''}`}
                  onClick={() => setContractsTab('explorer')}
                >
                  Contract Explorer
                </button>
                <button
                  className={`gtd-subtab${contractsTab === 'registered' ? ' active' : ''}`}
                  onClick={() => setContractsTab('registered')}
                >
                  Registered Contracts
                </button>
                <button
                  className={`gtd-subtab${contractsTab === 'rules' ? ' active' : ''}`}
                  onClick={() => setContractsTab('rules')}
                >
                  Execution Rules
                </button>
              </div>

              {contractsTab === 'explorer' && <ContractExplorer />}
              {contractsTab === 'registered' && (
                <RegisteredContractsList walletAddress={walletAddress} />
              )}
              {contractsTab === 'rules' && walletAddress && (
                <ExecutionRuleManager walletAddress={walletAddress} />
              )}
              {contractsTab === 'rules' && !walletAddress && (
                <div className="gtd-notice info">ℹ Connect a wallet to manage execution rules.</div>
              )}
            </div>
          )}

          {/* ── TRUST GROUPS ───────────────────────────────────────────── */}
          {activeSection === 'trust-groups' && (
            <div>
              <div className="gtd-section-header">
                <h2 className="gtd-section-title">Trust Groups</h2>
                <p className="gtd-section-subtitle">
                  Manage private sessions and trusted peer groups
                </p>
              </div>
              {walletAddress && contractClient && readOnlyClient ? (
                <div className="gtd-embed">
                  <TrustGroupManager
                    walletAddress={walletAddress}
                    contractClient={contractClient}
                    readOnlyClient={readOnlyClient}
                    onClose={() => {}}
                  />
                </div>
              ) : (
                <div className="gtd-notice info">
                  ℹ Connect a wallet to manage trust groups.
                </div>
              )}
            </div>
          )}

        </main>
      </div>

      {/* ── Mobile bottom tabs ────────────────────────────────────────── */}
      <div className="gtd-mobile-tabs">
        {visibleSections.map(s => (
          <button
            key={s.id}
            className={`gtd-mobile-tab${activeSection === s.id ? ' active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            <span className="m-icon">{s.icon}</span>
            <span className="m-label">{s.shortLabel}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── DeFindex vault card sub-component ─────────────────────────────────────────

const DefindexVaultCard: React.FC<{
  balance: DefindexVaultBalance;
  onDeposit: () => void;
}> = ({ balance, onDeposit }) => {
  const hasPosition = parseFloat(balance.shareBalance || '0') > 0;

  return (
    <div className="gtd-vault-card">
      <div className="gtd-vault-card-header">
        <div>
          <div className="gtd-vault-card-name">{balance.vaultName}</div>
          <div className="gtd-vault-card-addr">
            {balance.vaultAddress.slice(0, 8)}…{balance.vaultAddress.slice(-6)}
          </div>
        </div>
        <span className="gtd-vault-badge">DeFindex</span>
      </div>

      {balance.error ? (
        <div className="gtd-notice error" style={{ marginBottom: 0 }}>⚠ {balance.error}</div>
      ) : (
        <>
          {/* Combined total */}
          <div className="gtd-vault-row">
            <span className="rk">Your XLM Value</span>
            <span className={`rv${hasPosition ? ' big' : ''}`}>
              {parseFloat(balance.underlyingXlm).toFixed(4)} XLM
            </span>
          </div>

          {/* Wallet / passkey breakdown — shown when both sources contributed */}
          {balance.walletUnderlyingXlm !== undefined && balance.passkeyUnderlyingXlm !== undefined && (
            <div style={{
              display: 'flex', gap: 6, margin: '4px 0 8px',
              background: 'rgba(79,70,229,.05)', borderRadius: 6, padding: '6px 8px',
            }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--gt-muted)', marginBottom: 2 }}>💳 Wallet</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gt-dark)' }}>
                  {parseFloat(balance.walletUnderlyingXlm).toFixed(4)} XLM
                </div>
              </div>
              <div style={{ width: 1, background: 'var(--gt-border)' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--gt-muted)', marginBottom: 2 }}>🔑 Passkey</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gt-dark)' }}>
                  {parseFloat(balance.passkeyUnderlyingXlm).toFixed(4)} XLM
                </div>
              </div>
            </div>
          )}

          <div className="gtd-vault-row">
            <span className="rk">Share Tokens</span>
            <span className="rv">{parseFloat(balance.shareBalanceDisplay).toFixed(4)}</span>
          </div>
          <div className="gtd-vault-row">
            <span className="rk">Total Supply</span>
            <span className="rv">{(Number(balance.totalSupply) / 10_000_000).toFixed(4)}</span>
          </div>
          <div className="gtd-vault-row">
            <span className="rk">Total Managed</span>
            <span className="rv">{(Number(balance.totalManagedFundsStroops) / 10_000_000).toFixed(4)} XLM</span>
          </div>
          {balance.allocations.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {balance.allocations.map((a, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--gt-muted)', marginTop: 3 }}>
                  Asset: {(a.asset || '').slice(0, 12)}… · Idle: {(Number(a.idle_amount || 0) / 10_000_000).toFixed(4)} XLM
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="gtd-vault-actions">
        <button
          className="gtd-btn gtd-btn-primary gtd-btn-sm"
          onClick={onDeposit}
          title="Execute a pending deposit into this vault"
        >
          ⬆ Deposit
        </button>
        <a
          href={`https://stellar.expert/explorer/testnet/contract/${balance.vaultAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="gtd-btn gtd-btn-ghost gtd-btn-sm"
        >
          🔍 Explorer
        </a>
      </div>
    </div>
  );
};
